import { useEffect, useRef, useState } from 'react';

interface Point { icon: string; title: string; desc: string; }

interface WhyChooseUsProps {
  points: Point[];
  title?: string;
  subtitle?: string;
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function WhyChooseUs({
  points,
  title = 'Built for Programs That Cannot Fail',
  subtitle = 'The standards behind every OTT engagement.',
}: WhyChooseUsProps) {
  const { ref, visible } = useReveal();

  return (
    <section
      ref={ref}
      className="py-28 px-6 lg:px-12 xl:px-16"
      style={{ background: '#0A0A0A' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-start transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>

          {/* Left — sticky header */}
          <div className="lg:sticky lg:top-32">
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-gold" />
              <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">Why OTT</span>
            </div>
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight mb-6">
              {title}
            </h2>
            <p className="text-white/40 text-base font-sans leading-relaxed mb-10">
              {subtitle}
            </p>
            <div className="border-l-2 border-gold/40 pl-6">
              <p className="text-white/30 text-sm font-sans leading-relaxed italic">
                "OTT has been our go-to partner for press launch preparation across three consecutive model years."
              </p>
              <p className="text-gold/60 text-xs font-sans mt-3 tracking-wide uppercase">— Regional Marketing Director, Major OEM</p>
            </div>
          </div>

          {/* Right — points */}
          <div className="flex flex-col gap-8">
            {points.map((pt, i) => (
              <div
                key={i}
                className={`flex gap-5 group transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-12 h-12 flex items-center justify-center border border-gold/20 bg-gold/5 group-hover:bg-gold/15 group-hover:border-gold/40 transition-colors shrink-0 mt-0.5">
                  <i className={`${pt.icon} text-gold text-lg`} />
                </div>
                <div>
                  <h3 className="font-serif text-white text-lg font-semibold mb-2 leading-snug">{pt.title}</h3>
                  <p className="text-white/40 text-sm font-sans leading-relaxed">{pt.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
